import React, { useState, useEffect } from 'react';
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  Clock,
  HardDrive,
  AlertCircle,
  CheckCircle,
  Calendar,
  FileArchive,
  Upload
} from 'lucide-react';
import './BackupPanel.css';

const BackupPanel = () => {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);

  useEffect(() => {
    loadBackups();
    loadStats();
    // Auto-refresh cada 30 segundos
    const interval = setInterval(() => {
      loadBackups();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadBackups = async () => {
    try {
      const response = await fetch('/api/backups/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Error al cargar backups');
      
      const data = await response.json();
      setBackups(data.backups || []);
      setError(null);
    } catch (err) {
      console.error('Error cargando backups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/backups/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Error al cargar estadísticas');
      
      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    }
  };

  const handleCreateBackup = async () => {
    if (creatingBackup) return;
    
    setCreatingBackup(true);
    try {
      const response = await fetch('/api/backups/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          dbName: 'whatsapp-panel',
          description: 'Manual'
        })
      });
      
      if (!response.ok) throw new Error('Error al crear backup');
      
      const data = await response.json();
      alert('✅ ' + data.message);
      
      // Recargar después de 3 segundos
      setTimeout(() => {
        loadBackups();
        loadStats();
      }, 3000);
    } catch (err) {
      console.error('Error creando backup:', err);
      alert('❌ Error al crear backup: ' + err.message);
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      // Usar enlace directo con token en query string
      const token = localStorage.getItem('token');
      const url = `/api/backups/download/${filename}?token=${token}`;
      
      // Crear enlace temporal y hacer click
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error descargando backup:', err);
      alert('❌ Error al descargar backup: ' + err.message);
    }
  };

  const handleDelete = async (filename) => {
    try {
      const response = await fetch(`/api/backups/delete/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Error al eliminar backup');
      
      alert('✅ Backup eliminado exitosamente');
      setShowConfirmDelete(false);
      setSelectedBackup(null);
      loadBackups();
      loadStats();
    } catch (err) {
      console.error('Error eliminando backup:', err);
      alert('❌ Error al eliminar backup: ' + err.message);
    }
  };

  const handleRestore = async (filename) => {
    const confirmRestore = window.confirm(
      '⚠️ ADVERTENCIA: Esta acción sobrescribirá TODOS los datos actuales de la base de datos.\n\n' +
      '¿Estás seguro de que deseas restaurar este backup?\n\n' +
      'Backup: ' + filename
    );
    
    if (!confirmRestore) return;
    
    try {
      const response = await fetch(`/api/backups/restore/${filename}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          dbName: 'whatsapp-panel'
        })
      });
      
      if (!response.ok) throw new Error('Error al restaurar backup');
      
      const data = await response.json();
      alert('✅ ' + data.message + '\n\n' + data.info);
    } catch (err) {
      console.error('Error restaurando backup:', err);
      alert('❌ Error al restaurar backup: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-DO', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return {
        date: date.toLocaleDateString('es-DO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        time: date.toLocaleTimeString('es-DO', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      };
    } catch {
      return { date: dateStr, time: '' };
    }
  };

  if (loading) {
    return (
      <div className="backup-panel">
        <div className="backup-loading">
          <RefreshCw className="spin" size={48} />
          <p>Cargando backups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backup-panel">
      {/* Header */}
      <div className="backup-header">
        <div className="backup-title">
          <Database size={32} />
          <h1>Copias de Seguridad MongoDB</h1>
        </div>
        <div className="backup-actions">
          <button
            className="btn-create-backup"
            onClick={handleCreateBackup}
            disabled={creatingBackup}
          >
            {creatingBackup ? (
              <>
                <RefreshCw className="spin" size={20} />
                Creando...
              </>
            ) : (
              <>
                <Upload size={20} />
                Crear Backup Manual
              </>
            )}
          </button>
          <button className="btn-refresh" onClick={loadBackups}>
            <RefreshCw size={20} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      {stats && (
        <>
          <div className="backup-stats">
            <div className="stat-card">
              <FileArchive size={24} />
              <div>
                <div className="stat-value">{stats.totalBackups}</div>
                <div className="stat-label">Total Backups</div>
              </div>
            </div>
            <div className="stat-card">
              <HardDrive size={24} />
              <div>
                <div className="stat-value">{stats.totalSizeFormatted}</div>
                <div className="stat-label">Espacio Usado</div>
              </div>
            </div>
            <div className="stat-card">
              <Clock size={24} />
              <div>
                <div className="stat-value">
                  {stats.lastBackup ? formatDate(stats.lastBackup) : 'N/A'}
                </div>
                <div className="stat-label">Último Backup</div>
              </div>
            </div>
            <div className="stat-card success">
              <CheckCircle size={24} />
              <div>
                <div className="stat-value">Automático</div>
                <div className="stat-label">Diario a las 3 AM</div>
              </div>
            </div>
          </div>

          {/* Historial reciente de backups */}
          {stats.backupHistory && stats.backupHistory.length > 0 && (
            <div className="backup-history">
              <h3>
                <Clock size={20} />
                Historial Reciente de Backups
              </h3>
              <div className="history-list">
                {stats.backupHistory.map((log, index) => (
                  <div key={index} className="history-item">
                    <div className="history-icon">
                      {log.includes('exitoso') ? (
                        <CheckCircle size={16} className="success-icon" />
                      ) : (
                        <AlertCircle size={16} className="error-icon" />
                      )}
                    </div>
                    <div className="history-text">{log}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Error */}
      {error && (
        <div className="backup-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Info */}
      <div className="backup-info">
        <AlertCircle size={20} />
        <div>
          <strong>Información importante:</strong>
          <ul>
            <li>Los backups se crean automáticamente todos los días a las 3 AM</li>
            <li>Se mantienen los últimos 30 días de backups</li>
            <li>Puedes crear backups manuales en cualquier momento</li>
            <li>La restauración sobrescribe todos los datos actuales</li>
          </ul>
        </div>
      </div>

      {/* Lista de Backups */}
      <div className="backup-list">
        <h2>
          <FileArchive size={24} />
          Backups Disponibles ({backups.length})
        </h2>
        
        {backups.length === 0 ? (
          <div className="no-backups">
            <Database size={48} />
            <p>No hay backups disponibles</p>
            <button className="btn-create-first" onClick={handleCreateBackup}>
              Crear primer backup
            </button>
          </div>
        ) : (
          <div className="backup-table">
            <table>
              <thead>
                <tr>
                  <th><Calendar size={16} /> Fecha de Creación</th>
                  <th><Clock size={16} /> Hora</th>
                  <th><FileArchive size={16} /> Nombre del Archivo</th>
                  <th><HardDrive size={16} /> Tamaño</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup, index) => {
                  const dateTime = formatDateShort(backup.created);
                  return (
                    <tr key={index} className="backup-row">
                      <td className="backup-date">
                        <div className="date-cell">
                          <Calendar size={16} className="date-icon" />
                          <span>{dateTime.date}</span>
                        </div>
                      </td>
                      <td className="backup-time">
                        <div className="time-cell">
                          <Clock size={14} className="time-icon" />
                          <span>{dateTime.time}</span>
                        </div>
                      </td>
                      <td className="filename">{backup.filename}</td>
                      <td className="backup-size">{backup.sizeFormatted}</td>
                      <td className="actions">
                        <button
                          className="btn-action btn-download"
                          onClick={() => handleDownload(backup.filename)}
                          title="Descargar"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          className="btn-action btn-restore"
                          onClick={() => handleRestore(backup.filename)}
                          title="Restaurar"
                        >
                          <RefreshCw size={18} />
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => {
                            setSelectedBackup(backup.filename);
                            setShowConfirmDelete(true);
                          }}
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de confirmación */}
      {showConfirmDelete && (
        <div className="modal-overlay" onClick={() => setShowConfirmDelete(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Confirmar Eliminación</h3>
            <p>¿Estás seguro de que deseas eliminar este backup?</p>
            <p className="backup-name">{selectedBackup}</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowConfirmDelete(false);
                  setSelectedBackup(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-confirm-delete"
                onClick={() => handleDelete(selectedBackup)}
              >
                <Trash2 size={18} />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupPanel;
